import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { clientService } from '@/services/ClientService'
import { clientKeys, siteKeys } from '@/hooks/queryKeys'
import type { SiteDto } from '@lib/dto/site.dto'
import type { CreateSiteInput, UpdateSiteInput } from '@lib/input/site.input'

/**
 * Site write operations. Like contacts, sites are only ever read through their
 * client, so every write invalidates that client's detail and the lists (which
 * carry a site count).
 */
function useInvalidateClient(): (clientId: string, siteId?: string) => void {
  const queryClient = useQueryClient()
  return (clientId: string, siteId?: string) => {
    void queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) })
    void queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    // A site is now also readable on its own, by the survey and boundary
    // screens. Writing one has to refresh that entry too, or those screens keep
    // showing the outline someone just replaced.
    if (siteId !== undefined) {
      void queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) })
    }
  }
}

interface AddSiteVariables {
  clientId: string
  input: CreateSiteInput
}

export function useAddSite(): UseMutationResult<
  SiteDto,
  Error,
  AddSiteVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ clientId, input }: AddSiteVariables) =>
      clientService.addSite(clientId, input),
    onSuccess: (_site, variables) => invalidate(variables.clientId),
  })
}

interface UpdateSiteVariables {
  id: string
  clientId: string
  input: UpdateSiteInput
}

export function useUpdateSite(): UseMutationResult<
  SiteDto,
  Error,
  UpdateSiteVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ id, input }: UpdateSiteVariables) =>
      clientService.updateSite(id, input),
    onSuccess: (_site, variables) =>
      invalidate(variables.clientId, variables.id),
  })
}

interface ArchiveSiteVariables {
  id: string
  clientId: string
  archived: boolean
}

export function useArchiveSite(): UseMutationResult<
  SiteDto,
  Error,
  ArchiveSiteVariables
> {
  const invalidate = useInvalidateClient()
  return useMutation({
    mutationFn: ({ id, archived }: ArchiveSiteVariables) =>
      clientService.setSiteArchived(id, archived),
    onSuccess: (_site, variables) =>
      invalidate(variables.clientId, variables.id),
  })
}
